import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { authApi } from '@wordpuppi/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ThemeToggle } from '@/components/ThemeToggle';

export const Route = createFileRoute('/verify-email')({
  validateSearch: (search: Record<string, unknown>): { email?: string } => ({
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
  component: VerifyEmailInterstitial,
});

function VerifyEmailInterstitial() {
  const { email: initialEmail } = Route.useSearch();
  const [email, setEmail] = useState(initialEmail ?? '');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState('');

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setStatus('sending');
    try {
      await authApi.resendVerification(email);
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Could not resend the email.');
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-[400px]">
        <CardHeader>
          <CardTitle className="text-center text-2xl">Check your email</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            {initialEmail ? (
              <>
                We&apos;ve sent a verification link to{' '}
                <span className="font-medium text-foreground">{initialEmail}</span>. Click
                it to activate your account, then sign in.
              </>
            ) : (
              <>
                We&apos;ve sent you a verification link. Click it to activate your account,
                then sign in.
              </>
            )}
          </p>

          <form onSubmit={handleResend} className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="email">Didn&apos;t get it? Resend to</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setStatus('idle');
                }}
                required
                autoComplete="email"
              />
            </div>

            {status === 'sent' && (
              <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground">
                If that address has an unverified account, a new verification email is on
                its way.
              </div>
            )}
            {status === 'error' && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="outline"
              disabled={status === 'sending'}
              className="w-full"
            >
              {status === 'sending' ? 'Sending...' : 'Resend verification email'}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            <Link to="/login" className="text-primary underline-offset-4 hover:underline">
              Back to sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
